/** Map Kevine GET /api/paths JSON → PathsPage view model. */

import { LIVE_DATA_SOURCE_LABEL } from './journeyApiFilters';

const STAGE_LABELS = {
  ticket_purchased: 'Ticket',
  scanned_in: 'Scan',
  pos_spent: 'POS',
  returned: 'Return',
};

function formatCount(value) {
  return Number(value || 0).toLocaleString('en-US');
}

function pathId(sequence = []) {
  return sequence.join('__') || 'unknown';
}

function humanStages(sequence = []) {
  return sequence.map((key) => STAGE_LABELS[key] || key);
}

function badgeForConfidence(confidence, count) {
  if (confidence === 'high') return 'high_confidence';
  if (confidence === 'low' && count === 0) return 'incomplete';
  if (confidence === 'low') return null;
  return null;
}

function barToneFor(confidence, count) {
  if (confidence === 'high') return 'success';
  if (count === 0) return 'incomplete';
  return 'primary';
}

function buildInsight(path) {
  const labels = humanStages(path.sequence).join(' → ');
  const confidence = path.data_confidence || 'medium';
  if ((path.ticket_count || 0) === 0) {
    return {
      insight: `${labels}: no measurable tickets yet (pipeline or identity gap).`,
      action: 'Keep monitoring until ACP/POS data flows into vi_events.',
    };
  }
  if (path.sequence.length === 1) {
    return {
      insight: `${labels} only — ${formatCount(path.ticket_count)} tickets never reached a later stage.`,
      action: 'Confirm ACP ingest before treating this as true no-show.',
    };
  }
  return {
    insight: `${labels}: ${formatCount(path.ticket_count)} tickets (${path.percentage ?? 0}%). Confidence: ${confidence}.`,
    action:
      confidence === 'low'
        ? 'Treat as directional until cross-platform customer_id is confirmed.'
        : 'Use this path to prioritise ops experiments.',
  };
}

export function mapPathsApiToView(api) {
  const total = Number(api.total_tickets || 0);
  const mappedPaths = (api.paths || []).map((path) => {
    const count = Number(path.ticket_count || 0);
    const percentage = path.percentage == null ? null : Number(path.percentage);
    const share = percentage == null ? (total > 0 ? count / total : 0) : percentage / 100;
    const copy = buildInsight(path);
    return {
      id: pathId(path.sequence),
      stages: humanStages(path.sequence),
      share,
      count,
      badge: badgeForConfidence(path.data_confidence, count),
      barTone: barToneFor(path.data_confidence, count),
      insight: copy.insight,
      action: copy.action,
      dataConfidence: path.data_confidence,
    };
  });

  // Prefer paths with volume first, then keep zero-count paths for transparency.
  mappedPaths.sort((a, b) => b.count - a.count || b.share - a.share);

  const incomplete = mappedPaths.find((path) => path.stages.length === 1) || mappedPaths[0];
  const notes = api.notes || [];
  const confidenceTitle = notes[0]
    ? notes[0]
    : total > 0
      ? `Live paths from vi_events — ${formatCount(total)} tickets in window.`
      : 'No tickets in this window yet.';

  const reachScan = mappedPaths
    .filter((path) => path.stages.includes('Scan'))
    .reduce((sum, path) => sum + path.share, 0);
  const reachPos = mappedPaths
    .filter((path) => path.stages.includes('POS'))
    .reduce((sum, path) => sum + path.share, 0);

  return {
    dataSourceLabel: LIVE_DATA_SOURCE_LABEL,
    confidence: {
      tone: notes.length ? 'warning' : 'success',
      title: confidenceTitle.length > 160 ? `${confidenceTitle.slice(0, 157)}…` : confidenceTitle,
    },
    listMeta: {
      title: 'Top paths',
    },
    paths: mappedPaths,
    distribution: [
      { id: 'scan', label: 'Reach scan', value: Math.min(reachScan, 1) },
      { id: 'pos', label: 'Spend at POS', value: Math.min(reachPos, 1) },
      {
        id: 'ticket_only',
        label: 'Ticket only',
        value: incomplete ? incomplete.share : 0,
      },
    ],
    incomplete: {
      title: 'Most incomplete',
      headline: incomplete
        ? `${incomplete.stages.join(' → ')} — ${formatCount(incomplete.count)} (${((incomplete.share || 0) * 100).toFixed(1)}%)`
        : 'No incomplete path yet',
      action: incomplete?.action || 'Wait for ACP/POS ingest before judging drop-off.',
    },
    notes,
    raw: api,
  };
}
